import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, HeartHandshake, FolderArchive, Sparkles, BarChart3, BookOpen, Landmark, Compass, Clapperboard, ShieldCheck, PenLine, Siren } from "lucide-react";
import { BrandMark } from "./BrandMark";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tip } from "./Tip";
import { RemindersPanel } from "./RemindersPanel";
import { SecuritySheet } from "./SecuritySheet";
import { VaultSwitcher } from "./VaultSwitcher";
import { PlanBadge, MemberChip, SecurityButtons, RemindersButton, ThemeToggle, CaptureActions } from "./NavbarActions";
import { useSecurity } from "../context/SecurityContext";
import { fetchReminders, fetchSubscription, faxUnread } from "../lib/api";
import { tr } from "../lib/i18n";
import { LangToggle } from "./LangToggle";
import { MobileNav } from "./MobileNav";

const NAV = [
  { to: "/vault", label: "Vault", Icon: FolderArchive },
  { to: "/planning", label: "Planning", Icon: HeartHandshake },
  { to: "/library", label: "Library", Icon: BookOpen },
  { to: "/accounts", label: "Accounts", Icon: Landmark },
  { to: "/reports", label: "Reports", Icon: BarChart3 },
  { to: "/esign", label: "E-sign", Icon: PenLine },
  { to: "/emergency", label: "Emergency", Icon: Siren },
  { to: "/guide", label: "Guide", Icon: Compass },
];

const PRIMARY = new Set(["/vault", "/planning", "/library", "/accounts"]); // labelled from 1280px; the rest stay icons (with tooltips) until very wide screens
const PAGE_TITLES = { "/vault": "Vault", "/planning": "Planning", "/library": "Estate library", "/accounts": "Accounts", "/reports": "Reports", "/guide": "Guide", "/pricing": "Plans & billing", "/tax-pack": "Tax pack", "/estate-tax": "Estate tax", "/marketing-kit": "Marketing kit", "/passwords": "Passwords", "/bereavement": "Bereavement pack", "/tribute": "Tribute", "/checkup": "Yearly check-up", "/help": "Help", "/admin": "Admin", "/brand": "Brand", "/audit": "Audit trail", "/policies": "Policy adoption", "/life-admin": "Life admin", "/executor-mode": "Executor mode", "/executor-preview": "Executor preview", "/memories": "Memories", "/inventory": "Home inventory", "/esign": "E-sign", "/emergency": "Emergency" };
const usePageTitle = (pathname) => {
  useEffect(() => {
    const key = Object.keys(PAGE_TITLES).find((p) => pathname === p || pathname.startsWith(p + "/"));
    document.title = key ? `${PAGE_TITLES[key]} · HearthVault` : "HearthVault";
    return () => { document.title = "HearthVault — the family document safe"; };
  }, [pathname]);
};

const NavLinks = ({ pathname, role, adminAllowed, newFaxes = 0 }) => {
  let items = role === "owner" || role === "admin" ? [...NAV, { to: "/marketing-kit", label: "Marketing", Icon: Clapperboard }] : NAV;
  if (adminAllowed) items = [...items, { to: "/admin", label: "Admin", Icon: ShieldCheck, accent: true }];
  const link = ({ to, label, Icon, accent }) => (
    <Link
      key={to}
      to={to}
      data-testid={`nav-${label.toLowerCase()}`}
      title={tr(label)}
      className={`${accent ? "inline-flex shrink-0" : "hidden lg:inline-flex shrink-0"} items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        pathname.startsWith(to) ? "bg-primary/10 text-primary" : accent ? "border border-primary/40 text-primary hover:bg-primary/10" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <span className="relative inline-flex"><Icon className="h-4 w-4 shrink-0" />
        {to === "/library" && newFaxes > 0 && <span className="absolute -top-2 -right-2.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-semibold grid place-items-center" title={`${newFaxes} new fax${newFaxes === 1 ? "" : "es"}`} data-testid="nav-library-fax-badge">{newFaxes}</span>}
      </span>
      {to === "/library" && newFaxes > 0 && <span className="sr-only">{newFaxes} new fax{newFaxes === 1 ? "" : "es"}</span>}
      <span className={pathname.startsWith(to) ? "inline whitespace-nowrap" : PRIMARY.has(to) ? "hidden xl:inline whitespace-nowrap" : "hidden min-[2300px]:inline whitespace-nowrap"}>{tr(label)}</span>
    </Link>
  );
  // Plain links scroll inside a clipped strip on narrow rows; accent links (Admin) always stay on-screen.
  return (
    <>
      <nav className="flex items-center gap-1 ml-2 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">{items.filter((i) => !i.accent).map(link)}</nav>
      {items.filter((i) => i.accent).map(link)}
    </>
  );
};

export const Navbar = ({ search, setSearch, onUpload, onForward, onCamera, onViewDocument }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  usePageTitle(pathname);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const security = useSecurity();

  const { data: reminders } = useQuery({ queryKey: ["reminders"], queryFn: fetchReminders, refetchInterval: 60000, enabled: security?.role !== "health_agent" });
  const { data: subscription } = useQuery({ queryKey: ["subscription"], queryFn: fetchSubscription });

  const isAgent = ["health_agent", "finance_agent"].includes(security?.role);
  const { data: faxNew } = useQuery({ queryKey: ["fax-unread"], queryFn: faxUnread, refetchInterval: 60000, enabled: Boolean(security?.unlocked) && !isAgent });
  const badge = (reminders?.upcoming_count || 0) + (reminders?.overdue_count || 0);

  useEffect(() => {
    const onOpen = (e) => {
      if (e.detail === "security") setSecurityOpen(true);
      if (e.detail === "reminders") setRemindersOpen(true);
    };
    window.addEventListener("hv:open", onOpen);
    // Deep link used by text/push reminders: /vault?reminders=1
    if (new URLSearchParams(window.location.search).get("reminders") === "1") {
      setRemindersOpen(true);
      const u = new URL(window.location.href); u.searchParams.delete("reminders"); window.history.replaceState({}, "", u.pathname + u.search + u.hash);
    }
    return () => window.removeEventListener("hv:open", onOpen);
  }, []);

  const handleViewReminder = (docId) => {
    setRemindersOpen(false);
    if (onViewDocument) onViewDocument(docId);
    else navigate(`/vault?doc=${docId}`);
  };

  return (
    <header data-testid="main-navbar" className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-border/80">
      <div className="max-w-7xl 2xl:max-w-[1800px] mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
        {!isAgent && <MobileNav items={security?.role === "owner" || security?.role === "admin" ? [...NAV, { to: "/marketing-kit", label: "Marketing", Icon: Clapperboard }] : NAV} />}
        <Link to={isAgent ? "/agent" : "/vault"} className="flex items-center gap-2.5 shrink-0">
          <BrandMark />
        </Link>

        {!isAgent && <NavLinks newFaxes={faxNew?.count || 0} pathname={pathname} role={security?.role} adminAllowed={Boolean(security?.unlocked && (security?.member?.admin_access ? security.member.admin_access.allowed : security?.role === "admin"))} />}

        {setSearch ? (
          <div className="hidden sm:block flex-1 min-w-[120px] max-w-[160px] xl:max-w-[240px] 2xl:max-w-md ml-1 sm:ml-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tr("Search titles, tags, amounts, names…")}
              className="pl-9 bg-card border-border rounded-full h-10"
            />
          </div>
        ) : (
          <div className="flex-1 flex justify-center px-2">
            <button type="button" data-testid="app-search-btn" onClick={() => window.dispatchEvent(new CustomEvent("hv:open", { detail: "search" }))} className="hidden min-[2100px]:flex items-center gap-2 w-full min-w-[160px] max-w-md h-10 rounded-full border border-border bg-card px-3 text-sm text-muted-foreground hover:border-primary hover:text-foreground transition-colors">
              <Search className="h-4 w-4 shrink-0" /> <span className="flex-1 text-left truncate whitespace-nowrap">{tr("Search the app…")}</span><kbd className="hidden lg:inline rounded border border-border px-1.5 py-0.5 text-[10px] font-mono">⌘K</kbd>
            </button>
          </div>
        )}

        <div className="flex items-center gap-1 sm:gap-2 ml-auto shrink-0">
          <Tip label={tr("Search the app (⌘K / Ctrl-K) — jump to any feature, page or document")}>
            <Button data-testid="app-search-icon-btn" variant="ghost" size="icon" aria-label={tr("Search the app")} onClick={() => window.dispatchEvent(new CustomEvent("hv:open", { detail: "search" }))} className={`rounded-full ${setSearch ? "sm:hidden" : "min-[2100px]:hidden"}`}><Search className="h-5 w-5" /></Button>
          </Tip>
          <span className="hidden 2xl:inline-flex"><PlanBadge plan={subscription?.effective_plan || subscription?.plan || "free"} trial={!!subscription?.trial} daysLeft={subscription?.trial_days_left || 0} /></span>
          <VaultSwitcher />
          <MemberChip member={security?.member} readOnly={security?.readOnly} onClick={() => setSecurityOpen(true)} />
          <SecurityButtons security={security} onOpenSecurity={() => setSecurityOpen(true)} />
          <RemindersButton badge={badge} onClick={() => setRemindersOpen(true)} />
          <span className="hidden lg:inline-flex"><LangToggle compact /></span>
          <span className="hidden sm:inline-flex"><ThemeToggle /></span>
          <CaptureActions onCamera={onCamera} onForward={onForward} onUpload={onUpload} readOnly={security?.readOnly} />
        </div>
      </div>

      <RemindersPanel open={remindersOpen} onClose={() => setRemindersOpen(false)} data={reminders} onView={handleViewReminder} />
      <SecuritySheet open={securityOpen} onClose={() => setSecurityOpen(false)} />
    </header>
  );
};
